// =====================================================
// SUBSCRIPTION PLANS & FREE TIER LIMITS
// Central config for all monetization gating logic
// =====================================================

export type PlanType = 'free' | 'monthly' | 'annual' | 'family';
export type SubscriptionStatus = 'free' | 'trial' | 'active' | 'cancelled' | 'past_due' | 'grace_period' | 'incomplete';

// --- Free tier limits ---
export const FREE_LIMITS = {
  /** Max scholarships a free user can save to their pipeline */
  MAX_SAVES: 5,
  /** Free users can browse all 842+ scholarships */
  CAN_BROWSE: true,
  /** Free users get basic profile only */
  CAN_USE_BASIC_PROFILE: true,
} as const;

// --- Features that require premium ---
export type PremiumFeature =
  | 'unlimited_saves'
  | 'kanban_board'
  | 'essay_templates'
  | 'rec_letter_toolkit'
  | 'guardian_dashboard'
  | 'match_percentage'
  | 'school_spotlight'
  | 'advanced_filters'
  | 'college_explorer';

/** Human-readable labels for upgrade prompts */
export const FEATURE_LABELS: Record<PremiumFeature, string> = {
  unlimited_saves: 'Unlimited Saved Scholarships',
  kanban_board: 'Full Application Workflow',
  essay_templates: 'Essay Templates & AI Feedback',
  rec_letter_toolkit: 'Recommendation Letter Toolkit',
  guardian_dashboard: 'Guardian Dashboard',
  match_percentage: 'Scholarship Match %',
  school_spotlight: 'School Spotlight Data',
  advanced_filters: 'Advanced Search Filters',
  college_explorer: 'Full College Explorer',
};

/** Brief descriptions for upgrade modal */
export const FEATURE_DESCRIPTIONS: Record<PremiumFeature, string> = {
  unlimited_saves: 'Save as many scholarships as you want to your pipeline.',
  kanban_board: 'Move applications through stages — from saved to submitted to results.',
  essay_templates: 'Access 20+ essay frameworks with annotated examples.',
  rec_letter_toolkit: 'Generate recommendation letter packets for your teachers.',
  guardian_dashboard: 'Let your parents or guardians track your progress.',
  match_percentage: 'See how well each scholarship matches your profile.',
  school_spotlight: 'See how many students from your school have won.',
  advanced_filters: 'Filter scholarships by GPA, major, ethnicity, and more.',
  college_explorer: 'Explore all 160+ colleges with advanced filters and comparison tools.',
};

// --- Pricing ---
export const PRICING = {
  monthly: {
    amount: 12.99,
    label: '$12.99/mo',
    interval: 'month' as const,
  },
  annual: {
    amount: 99.00,
    label: '$99/yr',
    savings: '$56.88',
    interval: 'year' as const,
  },
} as const;

// --- Trial ---
export const TRIAL = {
  /** Duration in days */
  DURATION_DAYS: 14,
  /** Show trial banner when this many days remain */
  BANNER_THRESHOLD_DAYS: 5,
} as const;
